import { StyledTextField } from "../../../../styled-mui";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMatch } from "react-router";
import { useJsApiLoader } from "@react-google-maps/api";
import { debounce } from "lodash-es";

import { CART } from "../../../../constants/routes";
import { GMAPS_API_LOADER_CONFIG } from "../../../../configs/common";
import GoogleSearchAutocomplete from "../../google-search-autocomplete/google-search-autocomplete";

import { STORES_LIST_GET_REQUEST_ACTION, STORES_RESET_ACTION } from "../../../../redux/actions/stores";
import {
  storeLocatorModalTypeSelector,
  storeLocatorProductDataSelector,
  storesListSelector,
  isLoadingStoresListSelector,
} from "../../../../redux/selectors/stores";
import storeLocatorService from "../../../../_foundation/apis/transaction/storeLocator.service";
import { modalType, searchType } from "../../../../types/store-types";
import { STORELISTRADIUS } from "../../../../_foundation/constants/common";
import { useSite } from "../../../../_foundation/hooks/useSite";
import { useCart } from "../../../../_foundation/hooks/use-cart";
import formsUtil from "../../../../utils/formsUtil";
import { StyledBox } from "@hcl-commerce-store-sdk/react-component";

interface PostalCodeAutoSuggestProps {
  value?: string;
  onAddressChange?: (address: string) => void;
  onAreaChange?: (area: string) => void;
  error?: boolean;
}

interface skuItemType {
  sku: string;
  quantity: string;
}

const getPostalCodeFromPlaceId = async (placeId?: string): Promise<string | undefined> => {
  if (!placeId || !window.google) return;

  const geocoder = new google.maps.Geocoder();

  return new Promise((resolve) => {
    geocoder.geocode({ placeId }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        const postalCode = results[0].address_components.find((c) => c.types.includes("postal_code"))?.long_name;

        resolve(postalCode);
      } else {
        resolve(undefined);
      }
    });
  });
};

const getAreaAndCountryFromPlaceId = async (placeId?: string): Promise<{ area?: string; country?: string }> => {
  if (!placeId || !window.google) return {}; // <-- always return an object

  const geocoder = new google.maps.Geocoder();

  return new Promise((resolve) => {
    geocoder.geocode({ placeId }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        const area =
          results[0].address_components.find((c) => c.types.includes("locality"))?.long_name ||
          results[0].address_components.find(
            (c) => c.types.includes("sublocality") || c.types.includes("sublocality_level_1")
          )?.long_name;

        const country = results[0].address_components.find((c) => c.types.includes("country"))?.long_name;

        resolve({ area, country });
      } else {
        resolve({});
      }
    });
  });
};

const getPostalCodeFromLatLng = async (lat: number, lng: number): Promise<string | undefined> => {
  if (!window.google) return;

  const geocoder = new google.maps.Geocoder();

  return new Promise((resolve) => {
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results) {
        for (const result of results) {
          const postal = result.address_components.find((c) => c.types.includes("postal_code"))?.long_name;

          if (postal) {
            resolve(postal);
            return;
          }
        }
      }
      resolve(undefined);
    });
  });
};

export const PostalCodeAutoSuggest: React.FC<PostalCodeAutoSuggestProps> = ({
  onAddressChange,
  error,
  value,
  onAreaChange,
}) => {
  const { isLoaded: gMapsApiLoaded } = useJsApiLoader(GMAPS_API_LOADER_CONFIG);
  const dispatch = useDispatch();
  const matchCartPage = useMatch(CART);
  const { mySite } = useSite();
  const { orderItems } = useCart();
  const modalTypeSelected = useSelector(storeLocatorModalTypeSelector);
  const productData = useSelector(storeLocatorProductDataSelector);
  const storesList = useSelector(storesListSelector);
  const loadingStoresList = useSelector(isLoadingStoresListSelector);

  const [postalCodeValue, setPostalCodeValue] = useState<string>("");
  const [productsDataParams, setProductsDataParams] = useState<skuItemType[]>([]);
  const [searchResult, setSearchResult] = useState<google.maps.places.Autocomplete>();
  const [nearestStoreOffset] = useState(0);
  const debouncedGetStoresList = useRef(debounce((value: string) => fetchStoresByPostalCode(value), 1000)).current;
  useEffect(() => {
    if (searchResult) {
      searchResult.setFields(["address_components", "formatted_address", "geometry", "name"]);
    }
  }, [searchResult]);

  useEffect(() => {
    if (typeof value === "string") {
      setPostalCodeValue(value);
    }
  }, [value]);

  useEffect(() => {
    if (matchCartPage) {
      const prod: skuItemType[] = orderItems
        .filter((o) => o.buyable === "true")
        .map((order) => ({ sku: order.partNumber, quantity: String(order.quantity) }));
      setProductsDataParams(prod);
    } else {
      const prod: skuItemType[] =
        productData?.map((sku) => ({
          sku: sku.sku,
          quantity: String(sku.quantity),
        })) || [];
      setProductsDataParams(prod);
    }
  }, [orderItems, productData]);

  useEffect(() => {
    return () => {
      debouncedGetStoresList.cancel();
    };
  }, []);

  const fetchStoresByPostalCode = async (postalCode: string) => {
    debouncedGetStoresList.cancel();
    if (!formsUtil.validateZipCode(postalCode)) {
      dispatch(STORES_RESET_ACTION());
      return;
    }

    try {
      const { lat, long } = await storeLocatorService.getLatLong(postalCode);
      const params = {
        widget: "StoreLocatorWidget",
        latitude: String(lat),
        longitude: String(long),
        radius: mySite?.storeCfg?.userData?.STORELISTRADIUS ?? STORELISTRADIUS,
        siteLevelStoreSearch: false,
        maxItems: modalTypeSelected === modalType.Availability && nearestStoreOffset === 0 ? 3 : undefined,
      };

      dispatch(
        STORES_LIST_GET_REQUEST_ACTION({
          parameters: params,
          type: searchType.geoLocalisation,
          productData: productsDataParams,
          productStoreId: mySite.catalogStoreID,
        })
      );
    } catch (err) {
      console.error("Failed to fetch stores: ", err);
      dispatch(STORES_RESET_ACTION());
    }
  };

  const handlePostalCodeChange = async (value: string) => {
    setPostalCodeValue(value);
    onAddressChange?.(value);

    if (!value || !formsUtil.validateZipCode(value) || !window.google) {
      onAreaChange?.(""); // clear area if invalid
      return;
    }

    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: value }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        const area =
          results[0].address_components.find((c) => c.types.includes("locality"))?.long_name ||
          results[0].address_components.find(
            (c) => c.types.includes("sublocality") || c.types.includes("sublocality_level_1")
          )?.long_name;

        if (area) {
          onAreaChange?.(area);
        }
      }
    });
  };

  const handleAutocompleteSelect = async (
    search: google.maps.places.Autocomplete,
    place?: google.maps.places.PlaceResult
  ) => {
    debouncedGetStoresList.cancel();

    const selectedPlace = place || search.getPlace();
    if (!selectedPlace) return;

    let postalCode = selectedPlace.address_components?.find((c) => c.types.includes("postal_code"))?.long_name;

    const area =
      selectedPlace.address_components?.find((c) => c.types.includes("locality"))?.long_name ||
      selectedPlace.address_components?.find((c) => c.types.includes("sublocality"))?.long_name;

    /**
     * CASE 1: City like "Paris" → no postal code
     */
    if (!postalCode && selectedPlace.geometry?.location) {
      const lat = selectedPlace.geometry.location.lat();
      const lng = selectedPlace.geometry.location.lng();

      postalCode = await getPostalCodeFromLatLng(lat, lng);
    }

    /**
     * CASE 2: Still missing → fallback via placeId
     */
    if (!postalCode && selectedPlace.place_id) {
      postalCode = await getPostalCodeFromPlaceId(selectedPlace.place_id);
    }

    if (!postalCode) {
      console.warn("Unable to resolve postal code for city");
      return;
    }

    setPostalCodeValue(postalCode);
    onAddressChange?.(postalCode);

    if (area) {
      onAreaChange?.(area);
    }

    fetchStoresByPostalCode(postalCode);
  };

  return (
    <>
      {gMapsApiLoaded && (
        // <GoogleSearchAutocomplete
        //   searchResult={searchResult}
        //   setSearchResult={setSearchResult}
        //   onPlaceSelect={handleAutocompleteSelect}>

        // </GoogleSearchAutocomplete>
        <StyledTextField
          variant="standard"
          focused
          className="styled-v2"
          required
          fullWidth
          placeholder="Entrez votre code postal"
          label={
            <>
              Code Postal
              <StyledBox component="span" color="primary.main" sx={{ pl: 1 }}>
                *
              </StyledBox>
            </>
          }
          value={postalCodeValue}
          onChange={(e) => handlePostalCodeChange(e.target.value)}
          error={error}
          helperText={error ? "Veuillez saisir votre code postal" : ""}
        />
      )}
    </>
  );
};
